import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { BonusTransactionDto, LinkedDeposit } from 'api/Transaction/Types/BonusTransactionDto'
import { TransactionBonusKey } from 'api/Transaction/Types/BonusType'
import { Link } from 'components/Link'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'

import './Create/transaction.scss'

export interface GridColumn extends GridColumnProps {
  fields?: string[]
  exportFn?(gridProps: GridCellProps): string
  show: boolean
  width?: number
}

const bonusTransactionsColumn: GridColumn[] = [
  {
    title: 'Reference',
    field: TransactionBonusKey.reference,
    cell: (props: GridCellProps) => (
      <td
        className={`${generateClassNameForReverseType(props.dataItem)} transaction-columns__link`}
      >
        <Link
          to={{
            pathname: generatePath('/transactions/:id', {
              id: props.dataItem.id,
            }),
          }}
        >
          {(props.dataItem as BonusTransactionDto).referenceId}
        </Link>
      </td>
    ),
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Client',
    field: TransactionBonusKey.client,
    cell: (props: GridCellProps): JSX.Element => {
      const client = props.dataItem?.client
      if (!client) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td
          className={`${generateClassNameForReverseType(props.dataItem)} transaction-columns__link`}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: client.id,
                slug: 'details',
              }),
            }}
          >
            {client.name}
          </Link>
        </td>
      )
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Wallet',
    field: TransactionBonusKey.wallet,
    cell: (props: GridCellProps): JSX.Element => {
      const wallet = props.dataItem?.wallet
      if (!wallet) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td
          className={`${generateClassNameForReverseType(props.dataItem)} transaction-columns__link`}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                id: wallet.id,
                slug: 'details',
              }),
            }}
          >
            {wallet.name}
          </Link>
        </td>
      )
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Trading Account',
    field: TransactionBonusKey.tradingAccount,
    cell: (props: GridCellProps): JSX.Element => {
      const tradingAccount = props.dataItem?.tradingAccount
      if (!tradingAccount) {
        return <td className={generateClassNameForReverseType(props.dataItem)}>N/A</td>
      }

      return (
        <td
          className={`${generateClassNameForReverseType(props.dataItem)} transaction-columns__link`}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                id: tradingAccount.id,
                slug: 'details',
              }),
            }}
          >
            {tradingAccount.login}
          </Link>
        </td>
      )
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Bonus Type',
    field: TransactionBonusKey.bonusType,
    cell: (props: GridCellProps): JSX.Element => {
      const bonusType = props.dataItem?.bonusType
      if (!bonusType) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{bonusType.name}</td>
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Base Type',
    field: TransactionBonusKey.baseType,
    cell: (props: GridCellProps): JSX.Element => {
      const baseType = props.dataItem?.bonusType?.baseType
      if (!baseType) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{baseType.name}</td>
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Bonus Status',
    field: TransactionBonusKey.bonusTransactionStatus,
    cell: (props: GridCellProps): JSX.Element => {
      const bonusTransactionStatus = props.dataItem?.bonusTransactionStatus
      if (!bonusTransactionStatus) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {bonusTransactionStatus.name}
        </td>
      )
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Amount',
    field: TransactionBonusKey.bonusAmount,
    show: true,
    cell: (props: GridCellProps): JSX.Element => {
      const amount = props.dataItem?.amount
      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'number')}>
          {amount.toFixed(2)}
        </td>
      )
    },
    className: 'transaction-columns__number',
    filter: 'text',
  },
  {
    title: 'Currency',
    field: TransactionBonusKey.currency,
    cell: (props: GridCellProps): JSX.Element => {
      const currency = props.dataItem?.wallet?.currency
      if (!currency) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{currency.id}</td>
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Reserved',
    field: TransactionBonusKey.reservedAmount,
    cell: (props: GridCellProps): JSX.Element => {
      const bonusReserved = props.dataItem?.bonusReserved
      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'number')}>
          {bonusReserved.toFixed(2)}
        </td>
      )
    },
    show: true,
    className: 'transaction-columns__number',
    filter: 'text',
  },
  {
    title: 'Lots Required',
    field: TransactionBonusKey.lotsRequired,
    cell: (props: GridCellProps): JSX.Element => {
      const lotsRequired = props.dataItem?.lotsRequired
      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'number')}>
          {lotsRequired}
        </td>
      )
    },
    show: true,
    className: 'transaction-columns__number',
    filter: 'text',
  },
  {
    title: 'Lots Traded',
    field: TransactionBonusKey.lotsTraded,
    cell: (props: GridCellProps): JSX.Element => {
      const lotsTraded = props.dataItem?.lotsTraded
      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'number')}>{lotsTraded}</td>
      )
    },
    show: true,
    className: 'transaction-columns__number',
    filter: 'text',
  },
  {
    title: 'Expiry Date',
    field: TransactionBonusKey.expiryDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.expiryDate, false)}
      </td>
    ),
    show: true,
    className: 'transaction-columns__date',
    filter: 'text',
  },
  {
    title: 'Linked Transactions',
    field: TransactionBonusKey.linkedDeposits,
    fields: [TransactionBonusKey.linkedDeposits],
    cell: (gridProps: GridCellProps): JSX.Element => {
      return (
        <td
          className={`${generateClassNameForReverseType(gridProps.dataItem)} transaction-columns__link`}
        >
          {((gridProps.dataItem as BonusTransactionDto).linkedDeposits || []).map(
            (link, index: number) => (
              <Link
                key={index}
                to={{
                  pathname: generatePath('/transactions/:id', {
                    id: link.transactionId,
                  }),
                }}
              >
                <div>{link.referenceId}</div>
              </Link>
            )
          )}
        </td>
      )
    },
    exportFn: (gridProps: GridCellProps): string => {
      const linkedDeposits: LinkedDeposit[] = gridProps.dataItem?.linkedDeposits || []

      return linkedDeposits.map((x: LinkedDeposit) => x.referenceId).join(',')
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Entity',
    field: TransactionBonusKey.tickmillCompany,
    cell: (props: GridCellProps): JSX.Element => {
      const tickmillCompany = props.dataItem?.client?.tickmillCompany
      if (!tickmillCompany?.id) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          <CompanyTag id={tickmillCompany.id}>{tickmillCompany.name}</CompanyTag>
        </td>
      )
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Comment',
    field: TransactionBonusKey.comment,
    cell: (props: GridCellProps): JSX.Element => {
      const comment = props.dataItem?.comment
      return <td className={generateClassNameForReverseType(props.dataItem)}>{comment || ''}</td>
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Date Created',
    field: TransactionBonusKey.createdDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.createdDate, true)}
      </td>
    ),
    show: true,
    className: 'transaction-columns__date',
    filter: 'text',
  },
  {
    title: 'Date Updated',
    field: TransactionBonusKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    show: true,
    className: 'transaction-columns__date',
    filter: 'text',
  },
]

export default bonusTransactionsColumn
